import css from './code-style-sheet';
import dateFns from 'date-fns';
import Debug from 'ace-ui/ace-debug';
import merge from 'lodash/mergeWith';
import moment from 'moment';

const debug = new Debug('ContentCard');

const outputDateFormat = (datetime) => {
  if (!datetime || !datetime.ISOstring) {
    return;
  }

  let today = new Date();
  let date = new Date(datetime.ISOstring);

  if (dateFns.isToday(date)) {
    return moment (datetime.ISOstring).format('LT');
  }

  if (datetime.shortDateFormat) {
    if (dateFns.differenceInCalendarDays(today, date) < 30) {
      return dateFns.differenceInCalendarDays(today, date) + 'd';
    }

    if (dateFns.differenceInCalendarMonths(today, date) < 12) {
      return dateFns.differenceInCalendarMonths(today, date) + 'mo';
    }

    return dateFns.differenceInCalendarYears(today, date) + 'y';
  } else {
    return moment(datetime.ISOstring).format('L');
  }
};

export default class ContentCard {
  constructor (settings) {
    this.thumbnail = settings.thumbnail;
    let configs = settings.configurations;
    delete settings.configs;
    let card = ContentCard.normalize (JSON.parse (JSON.stringify (settings)));

    ContentCard.optionsSanitizer (card);

    if (this.thumbnail) {
      typeof this.thumbnail === 'string'
        ? this.thumbnail = card.thumbnail
        : Object.assign(this.thumbnail, card.thumbnail);
    }

    this.abstract = card.abstract;
    this.alias = card.alias;
    this.author = card.author;
    this.classifiers = card.classifiers;
    this.color = card.color;
    this.contentType = card.contentType;
    this.configurations = configs;
    this.datetime = card.datetime;
    this.label = card.label;
    this.tags = card.tags || { value: [] };
  }

  tag (tag) {
    if (typeof tag === 'string') {
      tag = { value: tag };
    }

    if (!this.tags) {
      this.tags = { value: [] };
    }

    this.tags.value.push (tag);

    return this;
  }

  getContextual (...contexts) {
    contexts = contexts || [];

    let contextsToMerge = [JSON.parse (JSON.stringify (this), (key, value) => {
      if (key === 'configurations') {
        return;
      } else {
        return value;
      }
    })];

    contexts.forEach ((context) => {
      if (typeof context === 'string') {
        (this.configurations || []).forEach ((config) => {
          if (config.contexts.includes (context)) {
            config.fields && contextsToMerge.push (ContentCard.normalize (config.fields));
          }
        });
      } else {
        context && contextsToMerge.push (ContentCard.normalize (context));
      }
    });

    let contextual = merge (...contextsToMerge, (acc, src, key) => {
      if (key === 'classifiers') {
        if (acc) {
          src.push (...acc);
          acc.length = 0;
          let finds = {};

          src.forEach ((item, index) => {
            if (finds[item.className] === undefined) {
              finds[item.className] = index;
            } else {
              src[finds[item.className]] = merge (item, src[finds[item.className]]);
              src.splice (index, 1);
            }
          });
        }
      }
    });

    if (contextual.style) {
      css.add (contextual.style);
    }

    return new ContentCard (contextual);
  }

  sanitize (sanitizer) {
    for (const key in this) {
      if (['label', 'abstract', 'author'].includes (key)) {
        if (this[key] && this[key].value) {
          this[key].value = sanitizer (this[key].value);
        }
      }

      if ('contentType' === key) {
        if (this[key] && this[key].label) {
          this[key].label = sanitizer (this[key].label);
        }
      }

      if ('tags' === key) {
        this.tags.value = this.tags && this.tags.value && this.tags.value.map (val => sanitizer (val));
      }

      if ('classifiers' === key) {
        this.classifiers && this.classifiers.forEach ((classifier) => {
          classifier.value = classifier.value && classifier.value.map (val => sanitizer (val));
        });
      }
    }

    return this;
  }

  static normalize (cardData) {
    if (cardData.abstract && typeof cardData.abstract === 'string') {
      cardData.abstract = { value: cardData.abstract };
    }

    if (cardData.author && typeof cardData.author === 'string') {
      cardData.author = { value: cardData.author };
    }

    if (cardData.contentType && typeof cardData.contentType === 'string') {
      cardData.contentType = { label: cardData.contentType };
    }

    if (cardData.datetime) {
      if (typeof cardData.datetime === 'string') {
        let date = new Date (cardData.datetime);

        cardData.datetime = {
          ISOstring: date.toISOString()
        };
      } else if (cardData.datetime instanceof Date) {
        let date = cardData.datetime;

        cardData.datetime = {
          ISOstring: date.toISOString()
        };
      }

      if (cardData.datetime.ISOstring) {
        cardData.datetime.value = new Date (cardData.datetime.ISOstring);
      } else if (cardData.datetime.value) {
        if (typeof cardData.datetime.value === 'string') {
          cardData.datetime.value = new Date (cardData.datetime.value);
        }

        try {
          cardData.datetime.ISOstring = cardData.datetime.value.toISOString ();
        } catch (error) {
          delete cardData.datetime.ISOstring;
        }
      }

      if (isNaN (cardData.datetime.value)) {
        if (cardData.datetime.value !== undefined) {
          console.warn ('Content card normalization: .datetime was invalid date, skip it.');
          debug.log ('Invalid date', cardData.datetime.value);
        }

        delete cardData.datetime.value;
      }
    }

    if (cardData.label && typeof cardData.label === 'string') {
      cardData.label = { value: cardData.label };
    }

    if (cardData.tags) {
      if (Array.isArray (cardData.tags)) {
        cardData.tags = {
          value: cardData.tags
        };
      }

      if (!Array.isArray (cardData.tags.value)) {
        console.warn ('Content card normalization: .tags.value was not an Array, skip it');
        delete cardData.tags;
      }
    }

    if (cardData.thumbnail && typeof cardData.thumbnail === 'string') {
      if (cardData.thumbnail.startsWith ('http')) {
        cardData.thumbnail = { url: cardData.thumbnail };
      } else {
        cardData.thumbnail = { alias: cardData.thumbnail };
      }
    }

    return cardData;
  }

  static optionsSanitizer (card) {
    if (card.datetime && card.datetime.ISOstring) {
      if (!card.datetime.title) {
        card.datetime.title = moment (card.datetime.ISOstring).format ('LLLL');
      }
      card.datetime.value = outputDateFormat (card.datetime);
    }

    if (card.thumbnail) {
      let style = { };

      let hasWidth = card.thumbnail.hasOwnProperty ('width');
      let hasHeight = card.thumbnail.hasOwnProperty ('height');

      let height = hasHeight && card.thumbnail.height;
      let width = hasWidth && card.thumbnail.width;

      if (hasWidth !== hasHeight) {
        if (card.thumbnail.aspectRatio) {
          let r = card.thumbnail.aspectRatio.split(':').reduce ((prev, curr) => parseInt(prev) / parseInt(curr));

          if (hasWidth) {
            height = Math.round (width / r);
            hasHeight = true;
          } else {
            width = Math.round (height * r);
            hasWidth = true;
          }
        }
      }

      let shouldCollapse = !(card.thumbnail.url || card.thumbnail.alias) && (card.thumbnail.collapse === true);

      if (!shouldCollapse) {
        style.width = hasWidth ? width + 'px' : style.width;
        style.height = hasHeight ? height + 'px' : style.height;
      } else {
        style.width = 0;
        style.height = 0;
      }

      card.thumbnail.style = Object.assign ({}, card.thumbnail.style, style);
    }

    ['label', 'abstract'].forEach (name => {
      if (card[name] && card[name].hasOwnProperty ('rows')) {
        let style = {
          '-webkit-box-orient': 'vertical',
          '-webkit-line-clamp': card[name].rows + '',
          'display': '-webkit-box'
        };

        card[name].style = Object.assign ({}, card[name].style || {}, style);
      }
    });
  }
}